import React, { useEffect, useState } from 'react';
import { Box, Button, createTheme, Modal, ThemeProvider } from '@mui/material';
import { useGridMode } from 'contexts/GridModeContext';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import { useTableScopedOpen } from 'hooks/useTableScopedOpen';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';

const AutoFoldMessageModal = () => {
  const { tabletype, move, moveMsg } = useTableSelector((table) => table.game);
  const currentPlayerSeatId = useTableSelector((table) => table.currentPlayer.seatId);
  const { resetMttInactivity } = useGameService();

  const isActiveTable = useIsActiveTable();
  const gridMode = useGridMode();

  const [msg, setMsg] = useState('');
  const [isShow, setIsShow] = useState(false);
  // This modal portals to <body>, so a kept-alive background table's panel
  // could raise its auto-fold warning over the table the player is actually on.
  // Scope it to its own table (see hooks/useTableScopedOpen).
  const isOpen = useTableScopedOpen(isShow);

  const handleClose = () => {
    setIsShow(false);
    resetMttInactivity();
  };

  useEffect(() => {
    if (currentPlayerSeatId === +move && moveMsg.length > 0) {
      setMsg(moveMsg);
      setIsShow(true);
    }
  }, [move, moveMsg]);

  // A background CAROUSEL table must not sit on a raised warning: it can't
  // paint (it isn't the table on screen), and a stuck flag would swallow the
  // next warning this table raises once the player is actually here. The grid
  // is the opposite: every cell is visible at once and "active" only gates
  // sound, so a non-focused cell's warning must stay up — its Ok button is the
  // only thing that calls resetMttInactivity(), and clearing the flag here
  // would let the MTT inactivity fold proceed with the player never seeing it.
  useEffect(() => {
    if (!isActiveTable && !gridMode) setIsShow(false);
  }, [isActiveTable, gridMode]);

  return (
    <>
      {tabletype === 'm' && (
        <Modal open={isOpen}>
          <Box
            sx={{
              ...modalStyle,
              boxShadow: 10,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '2rem'
            }}>
            <div style={{ fontSize: '1.5rem', textAlign: 'center' }}>{msg}</div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '2rem',
                width: '100%'
              }}>
              <ThemeProvider theme={CancelBtnStyle}>
                <Button
                  sx={{ display: 'block', opacity: '.8', textTransform: 'uppercase' }}
                  variant='contained'
                  onClick={handleClose}>
                  Ok
                </Button>
              </ThemeProvider>
            </div>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default AutoFoldMessageModal;

const modalStyle = {
  position: 'absolute' as const,
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: '#12100c',
  color: 'white',
  padding: '2rem',
  width: 'min(90vw, 360px)',
  borderRadius: '12px',
  border: '1px solid rgba(212, 168, 83, 0.2)',
};

const CancelBtnStyle = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          color: 'white',
          backgroundColor: 'rgba(212, 168, 83, 0.2)',
          '&:hover': {
            color: 'white',
            backgroundColor: 'rgba(212, 168, 83, 0.3)'
          },
          '&:disabled': {
            color: 'white',
            opacity: '0.4'
          },
          fontSize: '1rem',
          fontWeight: '400',
          padding: '0.5rem 1rem',
          minWidth: 'unset',
          textTransform: 'none',
          width: '100%'
        }
      }
    }
  }
});
